import { Box, ListItem } from "@mui/material";
import { useToast } from "@qubit/autoparts";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { createProductSearchOptions } from "~/config/searchProductConfig";
import NavSearchInput from "~/features/navbar/NavSearchInput";

import { runAsyncSafely, searchProduct } from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectAutostoreGridIdOverride,
  selectIsAdjustingBins
} from "~/redux/selectors/inventorySelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import {
  binsApi,
  GetBinCompartmentsApiResponse
} from "~/redux/warehouse/bins.openApi";

import { BinOrProductResult } from "./AutostoreInventory";
import { useAutocompleteSelect } from "./useAutocompleteSelect";
import { useViewType } from "./useViewType";

export function ProductBinSearchInput() {
  const { t } = useTranslation();
  const trackedPageName = useMixpanelPageName();
  const [searchText, setSearchText] = useState<string | null>(null);
  const [autocompleteOptions, updateAutocompleteOptions] = useState<
    BinOrProductResult[]
  >([] as BinOrProductResult[]);
  const { viewType, isHoldsPage } = useViewType();

  const { errorToast } = useToast();

  const handleAutocompleteSelect = useAutocompleteSelect();

  const isAdjustingBins = useAppSelector(selectIsAdjustingBins);
  const workstation = useAppSelector(selectThisWorkstation);
  const gridIdOverride = useAppSelector(selectAutostoreGridIdOverride);

  const [getBinIds] = binsApi.useLazyGetBinCompartmentsQuery();

  // clear autocomplete options if view changes from bin to product or vice versa
  useEffect(() => updateAutocompleteOptions([]), [viewType]);

  const searchBinsByText = useCallback(
    async (searchText: string) => {
      const gridId = workstation?.autostoreGridId ?? gridIdOverride;
      if (!gridId) {
        return [];
      }

      try {
        return await getBinIds({
          searchText,
          gridId
        }).unwrap();
      } catch (err) {
        errorToast(getMessageFromRtkError(err));
        return [];
      }
    },
    [gridIdOverride, workstation?.autostoreGridId, errorToast, getBinIds]
  );

  const createBinSearchOptions = (binRecord: GetBinCompartmentsApiResponse) =>
    binRecord.map(
      (b: {
        autostore_bin_number: number;
      }): { displayText: string; type: "bin" } => ({
        displayText: String(b.autostore_bin_number),
        type: "bin"
      })
    );

  const autocompleteSearchBinsAndProducts = async (
    input: string | null
  ): Promise<void> => {
    // search bins
    if (input === null || input === "") {
      updateAutocompleteOptions([]);
    } else {
      const truncatedInput = input.slice(0, 1000); // avoid excessively long search strings
      const [binSearch, hits] = await Promise.all([
        searchBinsByText(truncatedInput),
        searchProduct(truncatedInput)
      ]);

      const hitsAsAutocompleteRecords = createProductSearchOptions({ hits });
      const binsAsAutocompleteRecords = createBinSearchOptions(binSearch);
      updateAutocompleteOptions([
        ...binsAsAutocompleteRecords,
        ...hitsAsAutocompleteRecords
      ]);
    }
  };

  const searchPlaceholder = isHoldsPage
    ? t("search holds by product or bin")
    : t("search products or bin");

  return (
    <NavSearchInput<BinOrProductResult>
      textInput={searchText || ""}
      setTextInput={(text) => setSearchText(text)}
      isAutocomplete
      searchPlaceholder={searchPlaceholder}
      autocompleteSearchCb={(input) => {
        void runAsyncSafely(autocompleteSearchBinsAndProducts(input));
      }}
      autocompleteOptions={autocompleteOptions}
      autocompleteLabelConstructor={(option: BinOrProductResult): string =>
        option.displayText
      }
      groupBy={(option) => t(option.type)}
      selectCb={(result: BinOrProductResult) => {
        handleAutocompleteSelect(result);
        mixpanelTrack({
          trackedPageName,
          type: "Select Search Result",
          label: "Product/Bin Search Result",
          eventProperties: result
        });
      }}
      clearTextOnSelect
      renderOption={(params, option) => (
        <ListItem {...params} key={option.displayText}>
          <Box mr={1}>{option.displayText}</Box>
        </ListItem>
      )}
      autofocus={!isAdjustingBins}
      maxLength={1000}
    />
  );
}
